/**
 * Serviço de Video Lessons
 * Comunicação com o backend para obter aulas em vídeo
 */

import i18n from '../config/i18n';
import { authenticatedFetch } from './authService';

/** Ano de residência a que a aula se dirige. A lista é a do CHECK da 064. */
export type LessonLevel = 'R1' | 'R2' | 'R3';

export interface VideoLesson {
  id: string;
  title: string;
  videoId: string;
  subTopicId: number;
  description: string | null;
  duration: number | null;
  thumbnail: string | null;
  moduleId: number | null;
  /** Nome de quem ministra. Não existe `instructorName` — ver o COMMENT da 064. */
  teacher: string | null;
  level: LessonLevel | null;
  specialtyId: number | null;
  instructorRole: string | null;
  instructorInstitution: string | null;
}

/** A aula somada ao que ESTE aluno já fez dela. */
export interface StudentLesson extends Omit<VideoLesson, 'subTopicId'> {
  moduleName: string | null;
  specialtyName: string | null;
  percent: number;
  positionSeconds: number;
  completed: boolean;
}

export interface ModuleProgress {
  id: number;
  name: string;
  accentColor: string | null;
  sortOrder: number;
  lessonCount: number;
  completedCount: number;
  percent: number;
  totalSeconds: number;
}

/** O hero da vitrine. `started: false` ⇒ é um convite, não uma retomada. */
export interface ResumeTarget {
  lessonId: string;
  title: string;
  moduleId: number | null;
  moduleName: string | null;
  positionSeconds: number;
  percent: number;
  started: boolean;
}

export interface LessonLibrary {
  modules: ModuleProgress[];
  unassigned: { lessonCount: number; completedCount: number; percent: number };
  resume: ResumeTarget | null;
  overall: { done: number; total: number; percent: number };
}

export interface LessonCatalogPage {
  items: StudentLesson[];
  total: number;
  specialties: Array<{ id: number; name: string; total: number }>;
  levels: Array<{ level: LessonLevel; total: number }>;
}

export interface LessonCatalogFilters {
  search?: string;
  specialtyId?: number;
  level?: LessonLevel;
  moduleId?: number;
  limit?: number;
  offset?: number;
}

export interface LessonProgressFeedback {
  lessonId: string;
  positionSeconds: number;
  percent: number;
  completed: boolean;
}

/** "Pratique este tema": o que existe de verdade sobre a especialidade da aula. */
export interface LessonPractice {
  specialty: { id: number; name: string } | null;
  questions: number;
  flashcards: number;
  case: { id: string; title: string } | null;
}

export interface CreateVideoLessonData {
  title: string;
  videoId: string;
  subTopicId: number;
  topicId: number;
  duration?: number;
  teacher?: string;
  moduleId?: number;
  level?: LessonLevel;
  specialtyId?: number;
  instructorRole?: string;
  instructorInstitution?: string;
}

export interface UpdateVideoLessonData {
  title?: string;
  videoId?: string;
  subTopicId?: number;
  duration?: number;
  teacher?: string;
  moduleId?: number | null;
  // `null` LIMPA o campo; ausente o preserva. É o que permite desfazer um nível
  // marcado por engano sem ir ao banco.
  level?: LessonLevel | null;
  specialtyId?: number | null;
  instructorRole?: string | null;
  instructorInstitution?: string | null;
}

export async function getVideoLessonsByUserTopic(): Promise<VideoLesson[]> {
  try {
    const response = await authenticatedFetch('/video-lessons', {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || i18n.t('errors.fetchLessons'));
    }

    const lessons: VideoLesson[] = await response.json();
    return lessons;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export async function getVideoLessonsByTopicId(topicId: number): Promise<VideoLesson[]> {
  try {
    const response = await authenticatedFetch(`/video-lessons/topic/${topicId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || i18n.t('errors.fetchLessons'));
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export async function createVideoLesson(data: CreateVideoLessonData): Promise<VideoLesson> {
  try {
    const response = await authenticatedFetch('/video-lessons', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || i18n.t('errors.createVideoLesson'));
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export async function updateVideoLesson(id: string, data: UpdateVideoLessonData): Promise<VideoLesson> {
  try {
    const response = await authenticatedFetch(`/video-lessons/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || i18n.t('errors.updateVideoLesson'));
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

export async function deleteVideoLesson(id: string): Promise<void> {
  try {
    const response = await authenticatedFetch(`/video-lessons/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || i18n.t('errors.deleteVideoLesson'));
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

// ── Tela de Aulas do aluno ───────────────────────────────────────────────────

/**
 * Mesmo `handle` de `studyFlashcardService`: traduz a falha do servidor para
 * mensagem de interface, e distingue "o servidor recusou" de "não houve
 * resposta".
 */
async function handle<T>(promise: Promise<Response>, errorKey: string): Promise<T> {
  try {
    const response = await promise;
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || i18n.t(errorKey));
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('common.serverConnectionError'));
  }
}

function toQuery(filters: LessonCatalogFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.specialtyId != null) params.set('specialtyId', String(filters.specialtyId));
  if (filters.level) params.set('level', filters.level);
  if (filters.moduleId != null) params.set('moduleId', String(filters.moduleId));
  if (filters.limit != null) params.set('limit', String(filters.limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** A vitrine inteira numa requisição: módulos, hero e progresso do curso. */
export async function getLessonLibrary(): Promise<LessonLibrary> {
  return handle(
    authenticatedFetch('/video-lessons/progress', { method: 'GET' }),
    'lessons.fetchError',
  );
}

/** "Todas as aulas" — a busca e os filtros são resolvidos no servidor. */
export async function getLessonCatalog(
  filters: LessonCatalogFilters = {},
): Promise<LessonCatalogPage> {
  return handle(
    authenticatedFetch(`/video-lessons/catalog${toQuery(filters)}`, { method: 'GET' }),
    'lessons.fetchError',
  );
}

/** Uma aula com o progresso do aluno — o que o player abre por deep link. */
export async function getStudentLesson(id: string): Promise<StudentLesson> {
  return handle(
    authenticatedFetch(`/video-lessons/lesson/${id}`, { method: 'GET' }),
    'lessons.fetchError',
  );
}

export async function getLessonPractice(id: string): Promise<LessonPractice> {
  return handle(
    authenticatedFetch(`/video-lessons/lesson/${id}/practice`, { method: 'GET' }),
    'lessons.fetchError',
  );
}

/**
 * O heartbeat do player.
 *
 * Manda POSIÇÃO, nunca percentual: quem calcula o quanto foi assistido — e,
 * portanto, o que conta como concluído — é o servidor.
 */
export async function saveLessonProgress(
  id: string,
  data: { positionSeconds: number; durationSeconds?: number; completed?: true },
): Promise<LessonProgressFeedback> {
  return handle(
    authenticatedFetch(`/video-lessons/${id}/progress`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    'lessons.progressError',
  );
}
